import React, { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Modal } from "@/components/Modals";
import { listCustomers } from "@/lib/crmApi";

// Guests (pax) and the customer for a table on the billing PC (owner, 2026-10-24):
// asked when a table is opened, like the phone's Customer step, and editable from
// the order strip. The customer can be skipped only when `canSkip` (the
// captain_customer_skip_allowed setting). Pax is always asked.
//
// Typing part of a mobile (3+ digits) or a name (2+ letters) lists the guests who
// match, like the phone's Customer step (owner: "my number is already there but
// it is not coming here"). When `captains` is given (a new table on the PC), the
// table's captain is picked here; empty = the signed-in person.

const PHONE = /^\d{10}$/;
const SEARCH_DELAY_MS = 250;

export default function GuestDetailsDialog({ initial, canSkip, token, title, saving, error, onSave, onSkip, onCancel, captains, selfName }) {
  const [guests, setGuests] = useState(Math.max(1, Number(initial?.guests) || 1));
  const [phone, setPhone] = useState(initial?.customer?.phone || "");
  const [name, setName] = useState(initial?.customer?.name || "");
  const [customerId, setCustomerId] = useState(initial?.customer?.id ?? null);
  // A guest already on the order is shown, not edited here: retyping their name
  // would change nothing on the server. "Change customer" picks another guest.
  const [locked, setLocked] = useState(initial?.customer?.id != null);
  const [captainId, setCaptainId] = useState(initial?.captainId ?? "");
  const [matches, setMatches] = useState([]);
  const [searchFailed, setSearchFailed] = useState(false);

  // Search as the guest is typed: mobile digits first, else the name. A full mobile
  // that matches exactly fills the name in by itself.
  const term = phone.length >= 3 ? phone : name.trim().length >= 2 && customerId == null ? name.trim() : "";
  useEffect(() => {
    if (locked || !term) { setMatches([]); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      listCustomers(term, token)
        .then(rows => {
          if (!live) return;
          setSearchFailed(false);
          const list = (rows || []).slice(0, 6);
          const exact = PHONE.test(phone) && list.find(c => String(c.phone || "").replace(/\D/g, "").slice(-10) === phone);
          if (exact) {
            setCustomerId(exact.id);
            setName(exact.name || "");
            setMatches([]);
          } else {
            setMatches(list);
          }
        })
        .catch(() => { if (live) { setMatches([]); setSearchFailed(true); } });
    }, SEARCH_DELAY_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [term, phone, locked, token]);

  const pick = customer => {
    setCustomerId(customer.id);
    setPhone(String(customer.phone || "").replace(/\D/g, "").slice(-10));
    setName(customer.name || "");
    setMatches([]);
  };
  const chosenCaptain = captainId === "" ? null : Number(captainId);

  const typedCustomer = name.trim() || phone.trim();
  const customerValid = name.trim() && PHONE.test(phone.trim());
  // Skip off: name + 10-digit mobile required. Skip on: a half-typed guest is still
  // refused, so a typo is never saved as a customer.
  const canSave = guests >= 1 && (customerValid || (canSkip && !typedCustomer));

  const save = () => {
    if (!canSave || saving) return;
    const customer = customerValid
      ? { ...(customerId != null ? { id: customerId } : {}), name: name.trim(), phone: phone.trim() }
      : null;
    onSave({ guests, customer, captainId: chosenCaptain });
  };

  return (
    <Modal
      title={title}
      onClose={onCancel}
      size="confirm"
      testId="guest-details-dialog"
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <button type="button" className="h-10 px-4 rounded-md border text-body" onClick={onCancel} data-testid="guest-details-cancel">
            Cancel
          </button>
          {canSkip && onSkip && (
            <button type="button" className="h-10 px-4 rounded-md border text-body" onClick={() => onSkip({ guests, captainId: chosenCaptain })} disabled={saving} data-testid="guest-details-skip">
              Skip customer
            </button>
          )}
          <button
            type="button"
            className="h-10 px-4 rounded-md bg-action text-action-foreground hover:bg-action/90 text-body font-medium disabled:opacity-50"
            onClick={save}
            disabled={!canSave || saving}
            data-testid="guest-details-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="text-meta font-medium text-muted-foreground mb-1.5">Guests</div>
          <div className="flex items-center gap-3">
            <button type="button" className="w-10 h-10 rounded-md border flex items-center justify-center disabled:opacity-40" onClick={() => setGuests(g => Math.max(1, g - 1))} disabled={guests <= 1} aria-label="One guest fewer" data-testid="guest-count-minus">
              <Minus className="w-4 h-4" />
            </button>
            <span className="text-lede font-semibold w-8 text-center" data-testid="guest-count">{guests}</span>
            <button type="button" className="w-10 h-10 rounded-md border flex items-center justify-center" onClick={() => setGuests(g => g + 1)} aria-label="One guest more" data-testid="guest-count-plus">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        {captains && (
          <label className="block">
            <span className="text-meta font-medium text-muted-foreground">Captain</span>
            <select
              className="mt-1 w-full h-10 rounded-md border px-3 text-body bg-background"
              value={captainId}
              onChange={e => setCaptainId(e.target.value)}
              data-testid="guest-captain"
            >
              <option value="">{selfName ? `${selfName} (this PC)` : "This PC"}</option>
              {captains.map(captain => (
                <option key={captain.id} value={captain.id}>{captain.name}</option>
              ))}
            </select>
          </label>
        )}
        {locked ? (
          <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2" data-testid="guest-linked">
            <div className="min-w-0">
              <div className="text-body font-medium truncate">{name}</div>
              <div className="text-meta text-muted-foreground">{phone}</div>
            </div>
            <button
              type="button"
              className="h-9 px-3 rounded-md border text-meta shrink-0"
              onClick={() => { setLocked(false); setName(""); setPhone(""); setCustomerId(null); }}
              data-testid="guest-change-customer"
            >
              Change customer
            </button>
          </div>
        ) : (<>
        <label className="block">
          <span className="text-meta font-medium text-muted-foreground">Mobile</span>
          <input
            className="mt-1 w-full h-10 rounded-md border px-3 text-body bg-background"
            inputMode="numeric"
            value={phone}
            onChange={e => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setCustomerId(null); }}
            placeholder="10-digit mobile"
            data-testid="guest-phone"
          />
        </label>
        <label className="block">
          <span className="text-meta font-medium text-muted-foreground">Name</span>
          <input
            className="mt-1 w-full h-10 rounded-md border px-3 text-body bg-background"
            value={name}
            onChange={e => { setName(e.target.value); setCustomerId(null); }}
            placeholder="Guest name"
            data-testid="guest-name"
          />
        </label>
        {matches.length > 0 && (
          <ul className="rounded-md border divide-y max-h-48 overflow-y-auto" data-testid="guest-matches">
            {matches.map(customer => (
              <li key={customer.id}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted"
                  onClick={() => pick(customer)}
                  data-testid={`guest-match-${customer.id}`}
                >
                  <span className="text-body font-medium truncate">{customer.name || "—"}</span>
                  <span className="text-meta text-muted-foreground tabular-nums shrink-0">{customer.phone}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        </>)}
        {searchFailed && <p className="text-meta text-warning" data-testid="guest-search-failed">Guest search is unavailable right now — type the mobile and name in full.</p>}
        {!canSkip && <p className="text-meta text-muted-foreground">The guest's name and mobile are required for this restaurant.</p>}
        {error && <p className="text-meta text-danger" data-testid="guest-details-error">{error}</p>}
      </div>
    </Modal>
  );
}
